/**
 * screens/jobs/jobTokenHistory.tsx
 * Per-job token history — the full log behind a job's "Token history" button.
 * Wired to GET /api/hire/v2/requisitions/:id/token-history. Premium + animated:
 *   • KPI cards: match runs · input tokens · output tokens · last run
 *   • a per-run comparison chart (input vs output, bars grow on mount, hover)
 *   • a paginated log (10/page, serial) of every run — tokens, trigger, user, time.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { View, Text, ScrollView, Pressable, ActivityIndicator, StyleSheet, Platform } from 'react-native';
import { useNavigation, useRoute, useFocusEffect } from '@react-navigation/native';
import type { RouteProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { useTheme } from '../../context/themeContext';
import { useBreakpoints, ContentMaxWidth } from '../../theme/responsive';
import { web } from '../../utils/web';
import { useHover } from '../../hooks/useHover';
import { Icon, Panel, Pagination, StatusChip, EmptyState } from '../../components/ui';
import { getJobTokenHistory } from '../../services/hire/jobTokenHistory';
import type { JobTokenHistory, JobTokenRun } from '../../schemas/hire/jobTokenHistory';
import type { AppStackParamList } from '../../navigation/types';

type Nav = NativeStackNavigationProp<AppStackParamList>;
type Rt = RouteProp<AppStackParamList, 'jobTokenHistory'>;

/* ── helpers ──────────────────────────────────────────────────────────── */
function rise(delay = 0): object {
  return web({ animation: 'sk-rise 480ms cubic-bezier(.2,.8,.2,1) both', animationDelay: `${delay}ms` });
}
function useMounted(): boolean {
  const [m, setM] = useState(Platform.OS !== 'web');
  React.useEffect(() => {
    if (Platform.OS !== 'web') return;
    const raf = requestAnimationFrame(() => setM(true));
    return () => cancelAnimationFrame(raf);
  }, []);
  return m;
}
function useCountUp(target: number, duration = 640): number {
  const [n, setN] = useState(Platform.OS === 'web' ? 0 : target);
  React.useEffect(() => {
    if (Platform.OS !== 'web') { setN(target); return; }
    let raf = 0; const start = performance.now();
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / duration);
      setN(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return n;
}
function fmt(n: number): string { return n.toLocaleString(); }
function fmtCompact(n: number): string {
  if (n >= 1000) { const k = n / 1000; return `${Number.isInteger(k) ? k : k.toFixed(1)}k`; }
  return String(Math.round(n));
}
function niceCeil(v: number): number {
  if (v <= 0) return 1;
  const p = Math.pow(10, Math.floor(Math.log10(v)));
  const n = v / p; const m = n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10;
  return m * p;
}
function fmtDate(iso: string): string { return new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }); }
function fmtTime(iso: string): string { return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }); }

// Model shown by default when a run didn't record its own (mirrors AI_MODEL env).
const DEFAULT_MODEL = 'claude-sonnet-4-6';

// Shared log-table column geometry (header + rows use the same defs).
const GAP = 16;
type Align = 'left' | 'right';
const COLUMNS: { key: string; label: string; flex?: number; width?: number; align: Align; padLeft?: number }[] = [
  { key: 'sn', label: 'S.No', width: 44, align: 'left' },
  { key: 'run', label: 'Run', flex: 1.1, align: 'left' },
  { key: 'model', label: 'Model', flex: 1.4, align: 'left' },
  { key: 'input', label: 'Input', flex: 0.8, align: 'left' },
  { key: 'output', label: 'Output', flex: 0.8, align: 'left' },
  { key: 'total', label: 'Total', flex: 0.7, align: 'left' },
  { key: 'trigger', label: 'Trigger', flex: 0.9, align: 'left' },
  { key: 'user', label: 'User', flex: 1.6, align: 'left' },
  { key: 'time', label: 'Exact time', width: 116, align: 'left' },
];
const col = (k: string) => COLUMNS.find((c) => c.key === k)!;
function colStyle(c: { flex?: number; width?: number; align: Align; padLeft?: number }): object {
  return {
    ...(c.width != null ? { width: c.width } : { flex: c.flex }),
    minWidth: 0,
    alignItems: c.align === 'right' ? 'flex-end' : 'flex-start',
    ...(c.padLeft ? { paddingLeft: c.padLeft } : {}),
  };
}

/* ── KPI card ─────────────────────────────────────────────────────────── */
function Kpi({ icon, label, value, valueText, sub, emphasis, delay }: {
  icon: string; label: string; value?: number; valueText?: string; sub?: string; emphasis?: boolean; delay: number;
}) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii } = theme;
  const { hovered, hoverProps } = useHover();
  const { isMobile } = useBreakpoints();
  const n = useCountUp(value ?? 0);
  // Text values (dates) render at a smaller size than the big count numbers so
  // they fit on one line inside the 2-col mobile grid instead of wrapping.
  const valueFontSize = valueText != null ? (isMobile ? fontSize.xl : 22) : 25;
  return (
    <View
      {...hoverProps}
      style={[
        { flex: 1, minWidth: isMobile ? 150 : 160, paddingVertical: 16, paddingHorizontal: 16, borderRadius: radii.lg, borderWidth: 1, borderColor: hovered ? colors.lineStrong : colors.line, backgroundColor: emphasis ? colors.accentSoft : colors.card },
        rise(delay),
        web({ transition: 'transform 160ms ease, border-color 160ms ease, box-shadow 160ms ease' }),
        hovered && web({ transform: 'translateY(-3px)', boxShadow: '0 12px 30px -18px rgba(0,0,0,0.4)' }),
      ]}
    >
      {/* Reserve two label lines so the values align across the grid whether or
          not a label wraps ("Output tokens" vs "Last run"). */}
      <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 8, marginBottom: 10, minHeight: 30 }}>
        <View style={{ width: 26, height: 26, borderRadius: 8, alignItems: 'center', justifyContent: 'center', backgroundColor: emphasis ? colors.accent : colors.paper2, borderWidth: emphasis ? 0 : 1, borderColor: colors.line }}>
          <Icon name={icon} size={14} color={emphasis ? colors.onAccent : colors.ink3} />
        </View>
        <Text style={{ flex: 1, fontSize: 11, lineHeight: 14, fontWeight: fontWeight.semibold, letterSpacing: 0.4, textTransform: 'uppercase', color: colors.ink4 }}>{label}</Text>
      </View>
      <Text numberOfLines={1} style={{ fontSize: valueFontSize, fontWeight: fontWeight.bold, color: emphasis ? colors.accentInk : colors.ink, letterSpacing: -0.6 }}>{valueText ?? fmt(n)}</Text>
      {sub ? <Text numberOfLines={1} style={{ fontSize: fontSize.xs, color: colors.ink3, marginTop: 4 }}>{sub}</Text> : null}
    </View>
  );
}

/* ── chart column (per run) ───────────────────────────────────────────── */
function Bar({ run, max, height, colorsBy, mounted, index, showLabel }: {
  run: JobTokenRun; max: number; height: number; colorsBy: { input: string; output: string }; mounted: boolean; index: number; showLabel: boolean;
}) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, font, radii } = theme;
  const { hovered, hoverProps } = useHover();
  const scale = (v: number) => (max > 0 ? (v / max) * height : 0);
  const total = run.totalTokens;
  return (
    <View {...hoverProps} style={[{ flex: 1, alignItems: 'center', justifyContent: 'flex-end', minWidth: 6, height }, web({ cursor: 'pointer' })]}>
      {hovered && (
        <View
          pointerEvents="none"
          style={[
            { position: 'absolute', bottom: '100%', marginBottom: 8, minWidth: 168, padding: 10, borderRadius: radii.md, borderWidth: 1, borderColor: colors.lineStrong, backgroundColor: colors.card, zIndex: 30 },
            Platform.OS === 'web' ? ({ boxShadow: '0 14px 34px -16px rgba(0,0,0,0.5)' } as object) : null,
          ]}
        >
          <Text style={{ fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: colors.ink, marginBottom: 6 }}>Run {run.runNumber} · {fmtDate(run.ranAt)}</Text>
          <TipRow dot={colorsBy.input} label="Input tokens" value={run.inputTokens} />
          <TipRow dot={colorsBy.output} label="Output tokens" value={run.outputTokens} />
          <View style={{ height: 1, backgroundColor: colors.line, marginVertical: 6 }} />
          <TipRow label="Total tokens" value={total} bold />
          <Text style={{ fontSize: 11, color: colors.ink4, marginTop: 6 }}>{run.email ?? 'Unattributed'} · {run.triggeredBy}</Text>
        </View>
      )}
      {showLabel && total > 0 && (
        <Text style={{ fontFamily: font.mono, fontSize: 10, fontWeight: fontWeight.semibold, color: hovered ? colors.ink : colors.ink3, marginBottom: 4 }}>{fmtCompact(total)}</Text>
      )}
      <View style={{ width: '66%', maxWidth: 26, justifyContent: 'flex-end' }}>
        {[{ v: run.inputTokens, c: colorsBy.input, cap: true }, { v: run.outputTokens, c: colorsBy.output, cap: false }].map((s, i) => (
          <View
            key={i}
            style={[
              { height: mounted ? (s.v > 0 ? Math.max(3, scale(s.v)) : 0) : 0, backgroundColor: s.c, borderTopLeftRadius: s.cap ? 4 : 0, borderTopRightRadius: s.cap ? 4 : 0, opacity: hovered ? 1 : 0.95 },
              web({ transition: `height 640ms cubic-bezier(.2,.8,.2,1) ${index * 12}ms, opacity 150ms ease` }),
            ]}
          />
        ))}
      </View>
    </View>
  );
}
function TipRow({ dot, label, value, bold }: { dot?: string; label: string; value: number; bold?: boolean }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, font } = theme;
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 1 }}>
      {dot ? <View style={{ width: 7, height: 7, borderRadius: 4, backgroundColor: dot }} /> : <View style={{ width: 7 }} />}
      <Text style={{ fontSize: fontSize.xs, color: colors.ink3, flex: 1 }}>{label}</Text>
      <Text style={{ fontFamily: font.mono, fontSize: fontSize.xs, fontWeight: bold ? fontWeight.bold : fontWeight.semibold, color: colors.ink2 }}>{fmt(value)}</Text>
    </View>
  );
}

/* ── log row ──────────────────────────────────────────────────────────── */
function HeaderCell({ label }: { label: string }) {
  const { theme } = useTheme();
  const { colors, fontWeight } = theme;
  return <Text style={{ fontSize: 11, fontWeight: fontWeight.semibold, letterSpacing: 0.4, textTransform: 'uppercase', color: colors.ink4 }}>{label}</Text>;
}
const STATUS_TONE: Record<string, 'success' | 'warn' | 'danger' | 'neutral'> = {
  ok: 'success',
  fallback: 'warn',
  skipped_no_credits: 'warn',
  skipped_incremental: 'neutral',
  // legacy/short forms
  skipped: 'warn', failed: 'danger', no_credits: 'warn',
};

/**
 * Why a run spent what it spent. Zero tokens is a legitimate, common outcome —
 * Stage 4 is the only metered step — so the reason has to be on screen, or the
 * whole screen reads as broken.
 */
const STATUS_LABEL: Record<string, string> = {
  ok: 'rerank ran',
  fallback: 'AI failed → Stage-3 order',
  skipped_no_credits: 'Stage 4 skipped · no rerank credits',
  skipped_incremental: 'incremental · Stage 4 skipped by design',
};
function statusLabel(status: string): string { return STATUS_LABEL[status] ?? status; }

/** A rerank that never happened has no model. Showing a default one implies an
 *  AI call was made and returned nothing, which is the opposite of the truth. */
function modelLabel(model: string | null, status: string): string {
  if (model) return model;
  return status === 'ok' ? DEFAULT_MODEL : 'not called';
}

/** Label/value row for the mobile card meta list (string values). */
function MetaRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, font } = theme;
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 3 }}>
      <Text style={{ fontSize: fontSize.xs, color: colors.ink3, flex: 1 }}>{label}</Text>
      <Text numberOfLines={1} style={{ flexShrink: 1, textAlign: 'right', fontFamily: mono ? font.mono : font.sans, fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: colors.ink2 }}>{value}</Text>
    </View>
  );
}

function LogRow({ run, serial }: { run: JobTokenRun; serial: number }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, font, radii } = theme;
  const { hovered, hoverProps } = useHover();
  const { isMobile } = useBreakpoints();

  // Mobile: a stacked card — the 9-column run log can't fit at phone widths.
  if (isMobile) {
    return (
      <View style={{ padding: 14, borderRadius: radii.lg, borderWidth: 1, borderColor: colors.line, backgroundColor: colors.card, gap: 8 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
          <Text style={{ fontFamily: font.mono, fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: colors.ink4, minWidth: 18 }}>{serial}</Text>
          <View style={{ width: 30, height: 30, borderRadius: 9, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line }}>
            <Icon name="sparkles" size={14} color={colors.ink2} />
          </View>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: colors.ink }}>Run {run.runNumber}</Text>
            <Text style={{ fontSize: 11, color: colors.ink4 }}>{run.stage1Count}→{run.stage2Count} cands</Text>
          </View>
          <View style={{ alignItems: 'flex-end' }}>
            <Text style={{ fontFamily: font.mono, fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.ink }}>{fmt(run.totalTokens)}</Text>
            <Text style={{ fontSize: 10, color: colors.ink4 }}>tokens</Text>
          </View>
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 2 }}>
          <StatusChip tone={STATUS_TONE[run.status] ?? 'neutral'} label={run.triggeredBy} dot={false} />
          <Text style={{ fontSize: fontSize.xs, color: colors.ink3, flex: 1, minWidth: 0 }} numberOfLines={1}>{statusLabel(run.status)}</Text>
        </View>
        <View style={{ height: 1, backgroundColor: colors.line, marginVertical: 2 }} />
        <MetaRow label="Model" value={modelLabel(run.model, run.status)} mono />
        <MetaRow label="Input" value={fmt(run.inputTokens)} mono />
        <MetaRow label="Output" value={fmt(run.outputTokens)} mono />
        <MetaRow label="User" value={run.email ?? 'Unattributed'} />
        <MetaRow label="Exact time" value={`${fmtDate(run.ranAt)} · ${fmtTime(run.ranAt)}`} />
      </View>
    );
  }

  return (
    <View
      {...hoverProps}
      style={[
        { flexDirection: 'row', alignItems: 'center', gap: GAP, paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: colors.line, backgroundColor: hovered ? colors.paper2 : 'transparent' },
        web({ transition: 'background 130ms ease' }),
      ]}
    >
      <View style={colStyle(col('sn'))}><Text style={{ fontFamily: font.mono, fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: colors.ink4 }}>{serial}</Text></View>
      <View style={[colStyle(col('run')), { flexDirection: 'row', alignItems: 'center', gap: 8 }]}>
        <View style={{ width: 28, height: 28, borderRadius: 8, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line }}>
          <Icon name="sparkles" size={14} color={colors.ink2} />
        </View>
        <View style={{ minWidth: 0 }}>
          <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: colors.ink }}>Run {run.runNumber}</Text>
          <Text style={{ fontSize: 11, color: colors.ink4 }}>{run.stage1Count}→{run.stage2Count} cands</Text>
        </View>
      </View>
      <View style={colStyle(col('model'))}>
        <Text style={{ fontFamily: font.mono, fontSize: fontSize.xs, color: run.model ? colors.ink3 : colors.ink4 }} numberOfLines={1}>
          {modelLabel(run.model, run.status)}
        </Text>
        <Text style={{ fontSize: 10, color: colors.ink4, marginTop: 1 }} numberOfLines={1}>{statusLabel(run.status)}</Text>
      </View>
      <View style={colStyle(col('input'))}><Text style={{ fontFamily: font.mono, fontSize: fontSize.xs, color: colors.ink3 }}>{fmt(run.inputTokens)}</Text></View>
      <View style={colStyle(col('output'))}><Text style={{ fontFamily: font.mono, fontSize: fontSize.xs, color: colors.ink3 }}>{fmt(run.outputTokens)}</Text></View>
      <View style={colStyle(col('total'))}><Text style={{ fontFamily: font.mono, fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: colors.ink }}>{fmt(run.totalTokens)}</Text></View>
      <View style={colStyle(col('trigger'))}><StatusChip tone={STATUS_TONE[run.status] ?? 'neutral'} label={run.triggeredBy} dot={false} /></View>
      <View style={colStyle(col('user'))}><Text style={{ fontSize: fontSize.xs, color: run.email ? colors.ink2 : colors.ink4 }} numberOfLines={1}>{run.email ?? 'Unattributed'}</Text></View>
      <View style={colStyle(col('time'))}>
        <Text style={{ fontSize: fontSize.xs, color: colors.ink2 }}>{fmtDate(run.ranAt)}</Text>
        <Text style={{ fontFamily: font.mono, fontSize: 11, color: colors.ink4 }}>{fmtTime(run.ranAt)}</Text>
      </View>
    </View>
  );
}

/* ── screen ───────────────────────────────────────────────────────────── */
export default function JobTokenHistoryScreen() {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, font } = theme;
  const { isMobile } = useBreakpoints();
  const navigation = useNavigation<Nav>();
  const { params } = useRoute<Rt>();
  const id = params.id;

  const [data, setData] = useState<JobTokenHistory | null>(null);
  const [failed, setFailed] = useState(false);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  const load = useCallback(() => {
    const controller = new AbortController();
    getJobTokenHistory(id, controller.signal).then(setData).catch(() => setFailed(true));
    return () => controller.abort();
  }, [id]);
  useFocusEffect(load);

  const mounted = useMounted();
  const loading = data === null && !failed;
  const runs = data?.runs ?? [];
  const summary = data?.summary;
  // Chart series ascending by run number (oldest → newest, left → right).
  const series = useMemo(() => runs.slice().sort((a, b) => a.runNumber - b.runNumber), [runs]);
  const peak = useMemo(() => Math.max(0, ...series.map((r) => r.totalTokens)), [series]);
  const chartMax = niceCeil(peak || 1);
  const GRID = 4;
  const ticks = useMemo(() => Array.from({ length: GRID + 1 }, (_, i) => Math.round((chartMax / GRID) * (GRID - i))), [chartMax]);
  const colorsBy = { input: colors.accent, output: colors.teal };
  const chartHeight = 200;
  const AXIS_W = 48;

  const pageCount = Math.max(1, Math.ceil(runs.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageRuns = runs.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const hasRuns = runs.length > 0;

  return (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: isMobile ? 18 : 32, paddingBottom: 72 }}>
      <View style={{ width: '100%', maxWidth: ContentMaxWidth.default, alignSelf: 'center', gap: 20 }}>
        {/* Header */}
        <View style={[styles.header, rise(20), isMobile && { flexDirection: 'column', alignItems: 'flex-start', gap: 12 }]}>
          <View style={{ gap: 6, flex: 1, minWidth: 0 }}>
            <Pressable onPress={() => navigation.navigate('jobDetail', { id })} style={[{ flexDirection: 'row', alignItems: 'center', gap: 5 }, web({ cursor: 'pointer' })]}>
              <Icon name="chev-l" size={14} color={colors.ink4} />
              <Text style={{ fontSize: fontSize.xs, color: colors.ink4 }} numberOfLines={1}>{data?.job.title ?? 'Job'}</Text>
            </Pressable>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <View style={{ width: 34, height: 34, borderRadius: 10, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.accent }}>
                <Icon name="zap" size={17} color={colors.onAccent} />
              </View>
              <Text style={{ fontSize: 22, fontWeight: fontWeight.semibold, color: colors.ink, letterSpacing: -0.4 }}>Token history</Text>
            </View>
            {data ? (
              <Text style={{ fontFamily: font.mono, fontSize: fontSize.xs, color: colors.ink4 }}>{data.job.code} · match-run AI token spend · exact time · user</Text>
            ) : null}
          </View>
        </View>

        {loading ? (
          <View style={{ paddingVertical: 80 }}><ActivityIndicator size="large" color={colors.accent} /></View>
        ) : failed ? (
          <EmptyState icon="warn" title="Couldn't load token history" subtitle="Something went wrong fetching this job's match-run history. Try again in a moment." />
        ) : !hasRuns ? (
          <EmptyState
            icon="sparkles"
            title="No match runs yet"
            subtitle="Run a match on this job and every rerank's exact token spend — input, output, user and time — will appear here."
            action={{ label: 'Run match', icon: 'play', variant: 'primary', onPress: () => navigation.navigate('run', { requisitionId: id, autoRun: Date.now() }) }}
          />
        ) : (
          <>
            {/* KPI cards */}
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
              <Kpi icon="sparkles" label="Match runs" value={summary!.runCount} sub={`avg ${fmt(summary!.avgTokensPerRun)} tok/run`} emphasis delay={40} />
              <Kpi icon="download" label="Input tokens" value={summary!.inputTokens} sub={`${Math.round((summary!.inputTokens / Math.max(1, summary!.totalTokens)) * 100)}% of total`} delay={80} />
              <Kpi icon="upload" label="Output tokens" value={summary!.outputTokens} sub={`${Math.round((summary!.outputTokens / Math.max(1, summary!.totalTokens)) * 100)}% of total`} delay={120} />
              <Kpi icon="clock" label="Last run" valueText={summary!.lastRunAt ? fmtDate(summary!.lastRunAt) : '—'} sub={summary!.lastRunAt ? fmtTime(summary!.lastRunAt) : undefined} delay={160} />
            </View>

            {/* Comparison chart */}
            <View style={rise(180)}>
              <Panel title="Tokens per match run">
                <View style={{ flexDirection: 'row', gap: 16, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                  <LegendDot color={colorsBy.input} label={`Input · ${fmt(summary!.inputTokens)}`} />
                  <LegendDot color={colorsBy.output} label={`Output · ${fmt(summary!.outputTokens)}`} />
                  <Text style={{ marginLeft: 'auto', fontSize: fontSize.xs, color: colors.ink4 } as object}>{summary!.runCount} run{summary!.runCount === 1 ? '' : 's'} · peak {fmt(peak)} tok</Text>
                </View>
                <View style={{ flexDirection: 'row' }}>
                  <View style={{ width: AXIS_W, height: chartHeight, justifyContent: 'space-between', alignItems: 'flex-end', paddingRight: 8 }}>
                    {ticks.map((t) => <Text key={t} style={{ fontFamily: font.mono, fontSize: 10, color: colors.ink4, transform: [{ translateY: -5 }] }}>{fmtCompact(t)}</Text>)}
                  </View>
                  <View style={{ flex: 1 }}>
                    <View style={{ height: chartHeight, position: 'relative' }}>
                      {ticks.map((t, i) => (
                        <View key={t} pointerEvents="none" style={{ position: 'absolute', left: 0, right: 0, top: (chartHeight / GRID) * i, height: 1, backgroundColor: i === GRID ? colors.lineStrong : colors.line, opacity: i === GRID ? 1 : 0.6 }} />
                      ))}
                      <View style={{ height: chartHeight, flexDirection: 'row', alignItems: 'flex-end', gap: series.length > 40 ? 1 : 3 }}>
                        {series.map((r, i) => (
                          <Bar key={r.id} run={r} max={chartMax} height={chartHeight} colorsBy={colorsBy} mounted={mounted} index={i} showLabel={series.length <= 14} />
                        ))}
                      </View>
                    </View>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 8 }}>
                      <Text style={{ fontSize: 10, color: colors.ink4 }}>Run {series[0]?.runNumber ?? 1}</Text>
                      <Text style={{ fontSize: 10, color: colors.ink4 }}>Run {series[series.length - 1]?.runNumber ?? 1}</Text>
                    </View>
                  </View>
                </View>
              </Panel>
            </View>

            {/* Pagination */}
            <View style={[{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }, rise(220)]}>
              <Text style={{ fontSize: fontSize.sm, color: colors.ink3 }}>{runs.length} match run{runs.length === 1 ? '' : 's'} · newest first</Text>
              {pageCount > 1 && <Pagination page={safePage} pageCount={pageCount} onChange={setPage} />}
            </View>

            {/* Log */}
            <View style={rise(260)}>
              {isMobile ? (
                <View style={{ gap: 12 }}>
                  {pageRuns.map((r, i) => <LogRow key={r.id} run={r} serial={(safePage - 1) * PAGE_SIZE + i + 1} />)}
                </View>
              ) : (
                <Panel flush>
                  <View style={{ flexDirection: 'row', gap: GAP, paddingHorizontal: 16, paddingVertical: 11, backgroundColor: colors.paper2, borderTopLeftRadius: 10, borderTopRightRadius: 10 }}>
                    {COLUMNS.map((c) => <View key={c.key} style={colStyle(c)}><HeaderCell label={c.label} /></View>)}
                  </View>
                  {pageRuns.map((r, i) => <LogRow key={r.id} run={r} serial={(safePage - 1) * PAGE_SIZE + i + 1} />)}
                </Panel>
              )}
            </View>
          </>
        )}
      </View>
    </ScrollView>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  const { theme } = useTheme();
  const { colors, fontSize } = theme;
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 7 }}>
      <View style={{ width: 9, height: 9, borderRadius: 3, backgroundColor: color }} />
      <Text style={{ fontSize: fontSize.xs, color: colors.ink3 }}>{label}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  header: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 },
});
